import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/session.tsx';
import { onRoute, stepsFor, type Tour, type TourStep } from './steps.ts';
// Type only (erased): the module itself loads on demand below.
import type * as CatalogModule from './catalog.ts';
import {
  finished,
  left,
  loadProgress,
  moved,
  offerDismissed,
  saveProgress,
  started,
  type TourProgress,
} from './progress.ts';

// The overlay (positioning, focus, keyboard) and the catalog text download only when a tour starts or resumes: no
// cost for the first screen.
const TourOverlay = lazy(() => import('./TourOverlay.tsx'));
type Catalog = typeof CatalogModule;
const loadCatalog = (): Promise<Catalog> => import('./catalog.ts');

export interface RunningTour {
  tour: Tour;
  steps: readonly TourStep[];
  index: number;
}

interface TourApi {
  progress: TourProgress;
  running: RunningTour | null;
  /** Steps of a tour that the current unit's permissions open (empty: the profile cannot follow it). */
  stepsOf: (tour: Tour) => readonly TourStep[];
  start: (tourId: string, from?: 'inicio' | 'retomar') => void;
  go: (index: number) => void;
  leave: () => void;
  finish: () => void;
  dismissOffer: () => void;
}

const Ctx = createContext<TourApi | null>(null);

export function useTour(): TourApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTour fora do TourProvider');
  return v;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { session, unitId } = useSession();
  const userId = session?.user.id;
  const navigate = useNavigate();
  const location = useLocation();
  const [store, setStore] = useState(() => ({ userId, progress: loadProgress(userId) }));
  // Another user signing in on this browser gets their own record (state adjusted while rendering, not in an effect).
  if (store.userId !== userId) setStore({ userId, progress: loadProgress(userId) });
  const progress = store.progress;

  const update = useCallback(
    (f: (p: TourProgress) => TourProgress) =>
      setStore((s) => {
        const next = f(s.progress);
        saveProgress(s.userId, next);
        return { ...s, progress: next };
      }),
    [],
  );

  const unitPerms = useMemo(
    () => session?.units.find((u) => u.id === unitId)?.permissions ?? [],
    [session, unitId],
  );
  const anyUnitPerms = useMemo(() => session?.units.flatMap((u) => u.permissions) ?? [], [session]);
  const stepsOf = useCallback(
    (tour: Tour) => stepsFor(tour, unitPerms, anyUnitPerms),
    [unitPerms, anyUnitPerms],
  );

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const active = progress.active !== null;
  // A tour left open before a reload resumes once the catalog arrives.
  useEffect(() => {
    if (active && !catalog) void loadCatalog().then(setCatalog);
  }, [active, catalog]);

  const running = useMemo<RunningTour | null>(() => {
    const tour = progress.active && catalog ? catalog.tourById(progress.active.tourId) : undefined;
    if (!tour || !progress.active) return null;
    const steps = stepsOf(tour);
    if (!steps.length) return null;
    return { tour, steps, index: Math.min(progress.active.step, steps.length - 1) };
  }, [progress.active, stepsOf, catalog]);

  // Opens the screen of a step when the step is entered; frame steps (menu, header) stay on the screen already open.
  // Only then: a person who leaves for another screen during the tour is not pulled back.
  const open = useCallback(
    (step: TourStep | undefined) => {
      if (step?.route && !onRoute(location.pathname, step.route)) void navigate(step.route);
    },
    [location.pathname, navigate],
  );

  const start = useCallback(
    (tourId: string, from: 'inicio' | 'retomar' = 'inicio') => {
      void loadCatalog().then((c) => {
        setCatalog(c);
        const tour = c.tourById(tourId);
        const steps = tour ? stepsOf(tour) : [];
        if (!tour || !steps.length) return;
        const at = Math.min(from === 'retomar' ? (progress.paused[tourId] ?? 0) : 0, steps.length - 1);
        update((p) => started(p, tourId, at));
        open(steps[at]);
      });
    },
    [stepsOf, update, open, progress.paused],
  );

  const api = useMemo<TourApi>(
    () => ({
      progress,
      running,
      stepsOf,
      start,
      go: (index) => {
        update((p) => moved(p, index));
        open(running?.steps[index]);
      },
      leave: () => update(left),
      finish: () => update((p) => finished(p, new Date())),
      dismissOffer: () => update(offerDismissed),
    }),
    [progress, running, stepsOf, start, update, open],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {running ? (
        <Suspense fallback={null}>
          <TourOverlay />
        </Suspense>
      ) : null}
    </Ctx.Provider>
  );
}
